import { inject, Injector, Provider } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { RA_UI_INTL, RaUiIntl } from 'ra-ui';

/**
 * Liga os textos de "chrome" do design system (`RA_UI_INTL`) ao i18n do app
 * (ngx-translate). A ra-ui não depende de ngx-translate; este adapter é o único
 * ponto de ligação. Os getters resolvem a cada leitura, então troca de idioma
 * em runtime é refletida nos componentes.
 *
 * O `TranslateService` é resolvido de forma preguiçosa (via `Injector`) dentro
 * dos getters. Resolvê-lo avidamente no factory criaria uma dependência circular
 * de DI: `RaToastService` injeta `RA_UI_INTL`, e o `TranslateService` puxa o
 * `HttpClient`/interceptor (→ `AuthController` → `UtilService` → `RaToastService`).
 */
export function provideRaUiIntl(): Provider {
    return {
        provide: RA_UI_INTL,
        useFactory: (): RaUiIntl => {
            const injector = inject(Injector);
            const t = (): TranslateService => injector.get(TranslateService);
            return {
                get requiredField() {
                    return t().instant('common.requiredField');
                },
                get used() {
                    return t().instant('common.used');
                },
                get noOptionsFound() {
                    return t().instant('shared.select.noOptionsFound');
                },
                get clickToSeeMore() {
                    return t().instant('common.clickToSeeMore');
                },
                get imageClose() {
                    return t().instant('shared.imagesFullScreen.close');
                },
                get confirmation() {
                    return t().instant('common.confirmation');
                },
                get confirm() {
                    return t().instant('common.confirm');
                },
                get cancel() {
                    return t().instant('common.cancel');
                },
                get irreversibleAction() {
                    return t().instant('common.irreversibleAction');
                },
                get processing() {
                    return t().instant('common.processing');
                },
                get closeNotification() {
                    return t().instant('common.closeNotification');
                },
                get toastSuccess() {
                    return t().instant('common.success');
                },
                get toastError() {
                    return t().instant('common.error');
                },
                get toastWarning() {
                    return t().instant('common.warning');
                },
                get toastInfo() {
                    return t().instant('common.info');
                },
            };
        },
    };
}
